import React, { useState, useRef, useCallback } from 'react'
import { processUserFileUpload } from '../../../libs/services'
import { LoadingSpinner, PageTitle } from '../../../components/atoms'
import { logger } from '../../../utils'
import { useExcelPreprocessor } from '../../../components/molecules/ExcelPreProcessor/ExcelPreprocessor'

export const pageTitle = 'Add Students'

interface UserExcelImportError {
  status: 'Success' | 'Failed'
  error: string
  row?: number
}

interface UserExcelImportResponse {
  status: string
  successfulImports: {
    numberOfDuplicateRecords: number
    numberOfInsertedRecords: number
  }
  failedImports: number
  errors: UserExcelImportError[]
  totalRows: number
}

const AddStudents: React.FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<UserExcelImportError[]>([])
  const [successMessage, setSuccessMessage] = useState('')

  // Get the preprocessor function
  const { preprocessExcelFile } = useExcelPreprocessor()

  const handleButtonClick = (): void => {
    if (fileInputRef.current != null && !isLoading) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files != null && e.target.files.length > 0) {
      const file = e.target.files[0]
      await handleExcelUpload(file)
      e.target.value = ''
    }
  }

  const handleExcelUpload = async (file: File): Promise<void> => {
    setIsLoading(true)
    setUploadErrors([])
    setSuccessMessage('')

    try {
      // First preprocess the file
      const preprocessResult = await preprocessExcelFile(file)

      // Handle case with no valid rows to process
      if (preprocessResult.validRowCount === 0) {
        setSuccessMessage('0 students were successfully imported.')
        setIsLoading(false)
        return
      }

      // Use the processed file for upload if available, otherwise use original
      const fileToUpload = preprocessResult.processedFile ?? file

      // Send to backend for processing
      const response = await processUserFileUpload(fileToUpload)
      const data: UserExcelImportResponse = response.data

      // Set success message based on the response status
      const successfulImports = data.successfulImports.numberOfInsertedRecords

      if (data.status === 'success') {
        // Complete success
        setSuccessMessage(`${successfulImports} students were successfully imported.`)
      } else if (data.status === 'partial success' && data.successfulImports.numberOfInsertedRecords === 0) {
        // Backend recognizes this as a partial success, but if no students were uploaded than it is techincally a complete failure
        setSuccessMessage(
          `${successfulImports} students were successfully imported, ` +
          `${data.failedImports} students failed.`
        )
      } else if (data.status === 'partial success') {
        // Partial success - show successful and failed counts
        setSuccessMessage(
          `PARTIAL SUCCESS: ${successfulImports} students were successfully imported, ` +
          `${data.failedImports} students failed.`
        )
      } else {
        // Complete failure - should show 0 successful imports
        setSuccessMessage(`${successfulImports} students were successfully imported.`)
      }

      // Set error messages from backend
      setUploadErrors(data.errors)
    } catch (error: unknown) {
      if (error instanceof Error) {
        logger.error(error.message)
      } else {
        logger.error(JSON.stringify(error))
      }

      const errorMessages: UserExcelImportError[] = [
        {
          status: 'Failed',
          error: 'File processing failed. Please check your file format.'
        }
      ]
      setUploadErrors(errorMessages)
      setSuccessMessage('0 students were successfully imported.')
    } finally {
      setIsLoading(false)
    }
  }

  // Helper function to count error instances
  const getErrorCounts = useCallback((): Record<string, number> => {
    const errorCounts: Record<string, number> = {}

    uploadErrors.forEach((err) => {
      errorCounts[err.error] = (errorCounts[err.error] !== undefined ? errorCounts[err.error] : 0) + 1
    })

    return errorCounts
  }, [uploadErrors])

  // #region Drag Events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    if (!isLoading) {
      setIsDragging(true)
    }
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>): Promise<void> => {
    e.preventDefault()
    setIsDragging(false)

    if (isLoading) return

    const { files } = e.dataTransfer
    if (files.length > 0) {
      const file = files[0]
      await handleExcelUpload(file)
      e.dataTransfer.clearData()
    }
  }
  // #endregion

  return (
    <div className='flex flex-col h-full justify-between'>
      <PageTitle title={pageTitle} addScreenReaderH1={false} />

      {/* Main content */}
      <div className='flex-grow'>
        <div className='w-full px-10 py-12'>
          <h1 className='text-[72px] font-bold text-[rgba(52,232,158,0.75)]'>{pageTitle}</h1>
          <input
            type='file'
            accept='.xlsx, .csv'
            ref={fileInputRef}
            className='hidden'
            data-testid='file-input'
            onChange={(e) => {
              handleFileChange(e).catch((err) => logger.error(err))
            }}
          />
          {/* Drop zone with styled box and text */}
          <div
            role='button'
            tabIndex={0}
            className={`relative cursor-pointer transition-colors duration-200 flex flex-col items-center justify-center text-center
            ${isDragging ? 'bg-[rgba(52,232,158,0.25)] border-2 border-dashed border-[rgba(52,232,158,0.75)]' : 'bg-[#DFF8FF]'}
            w-full max-w-[90%] sm:max-w-[80%] md:max-w-[70%] lg:max-w-[60%] xl:max-w-[55%] h-[30vh] sm:h-[35vh] md:h-[40vh] lg:h-[45vh] xl:h-[40vh] rounded-lg shadow-[0_10px_30px_rgba(0,0,0,22)]`}
            onClick={handleButtonClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleButtonClick()
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={(e) => {
              handleDrop(e).catch((err) => logger.error(err))
            }}
          >
            <p className='text-2xl font-bold text-[#0D2E3B]'>UPLOAD STUDENTS HERE</p>
            <p className='text-md font-bold text-[#0D2E3B] mt-2'>Upload your .XLSX or .CSV file.</p>
            <p className='text-md font-bold text-[#0D2E3B]'>DRAG 'n' DROP or CLICK HERE to select from your device</p>

            {isLoading && (
              <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-75'>
                <div className='h-[100%] flex justify-center items-center'><LoadingSpinner className='w-1/4 min-w-[80px] max-w-[200px]' /></div>
              </div>
            )}
          </div>

          {/* Success message */}
          {successMessage !== '' && (
            <div className='mt-4 text-[rgba(52,232,158,1)] font-bold'>
              {successMessage}
            </div>
          )}

          {/* Error messages */}
          {uploadErrors.length > 0 && (
            <div className='mt-4 max-h-48 overflow-y-auto'>
              <ul className='list-disc list-inside text-red-500'>
                <div className='font-bold'>
                  TOTAL ERROR COUNT: {uploadErrors.length}
                </div>
                {Object.entries(getErrorCounts()).map(([errorType, count], index) => (
                  count >= 1
                    ? (
                      <li key={index}>
                        OOPS! {errorType} INSTANCES = {count}
                      </li>
                      )
                    : null
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      {/* Bottom Links */}
      <div className='px-10 mb-10'>
        <a
          href='https://dopa.ge/studentuploadforadmin'
          target='_blank'
          rel='noopener noreferrer'
          className='text-[rgba(52,232,158,1)] underline font-bold mr-4'
        >
          XLSX Upload Template
        </a>
        <a
          href='https://dopa.ge/studentuploadforadmins-instructions'
          target='_blank'
          rel='noopener noreferrer'
          className='text-[rgba(52,232,158,1)] underline font-bold'
        >
          Full Instructions
        </a>
      </div>
    </div>
  )
}

export default AddStudents
